import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { signOut } from "../lib/firebase";
import { MAX_NOTES_CHARS, type Deal, type DealVisibility } from "@docreview/shared";

const DEAL_TYPE_PLACEHOLDER = "Note, PPP, A/B Notes + DACA, Convertible Note + Warrants, etc.";

/** True for backend auth rejections (401/403) — recovery is re-auth, not retry. */
function isAuthError(e: unknown): boolean {
  return /^(401|403)\b/.test(e instanceof Error ? e.message : String(e));
}

/**
 * Turn an api.ts error into something a user can act on. Assumes the
 * `jsonRequest` "<status> <body>" shape (api.ts); other error shapes fall
 * through to their raw message. The backend's auth failures are the ones worth
 * special-casing — otherwise a 403 reads as an empty page and a dead button
 * (the outside-counsel incident).
 */
function friendlyApiError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const m = raw.match(/^(\d{3})\s+([\s\S]*)$/);
  if (!m) return raw;
  const status = m[1] ?? "";
  const body = m[2] ?? "";
  let code = body;
  try {
    const j = JSON.parse(body) as { error?: string; message?: string };
    code = j.error ?? j.message ?? body;
  } catch {
    /* not JSON */
  }
  if (status === "403") {
    if (code.includes("email_not_verified"))
      return "Your email isn't verified yet. Check your inbox for the verification link, then sign out and back in.";
    if (code.includes("domain_not_allowed"))
      return "Your email domain isn't enabled for this app yet. Contact an admin.";
    return "You don't have access to do that. Contact an admin.";
  }
  if (status === "401") return "Your session expired. Please sign out and sign in again.";
  return `Something went wrong (${status}). ${code}`;
}

export default function DealsPage() {
  const { user } = useAuth();
  const isAdmin = useAuth().isAdmin === true;
  // Off for everyone but the allowlisted user(s). When false this page
  // renders exactly as it always did — no tabs, no lock icons, no
  // checkbox — and the backend has already filtered private deals out of
  // the response.
  const canCreatePrivateDeals = useAuth().canCreatePrivateDeals;
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorIsAuth, setErrorIsAuth] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState<DealVisibility>("team");
  const privateTab = canCreatePrivateDeals && tab === "private";
  const visibleDeals = canCreatePrivateDeals
    ? deals.filter((d) => (d.visibility === "private") === privateTab)
    : deals;

  useEffect(() => {
    api
      .listDeals()
      .then(({ deals }) => setDeals(deals))
      .catch((e) => {
        setError(friendlyApiError(e));
        setErrorIsAuth(isAuthError(e));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Deals</h1>
        <button className="btn-primary" onClick={() => setShowNew(true)}>
          New deal
        </button>
      </div>
      {canCreatePrivateDeals && (
        <div className="flex gap-1 mb-4 border-b border-slate-200">
          {(["team", "private"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-sm -mb-px border-b-2 ${
                tab === t
                  ? "border-slate-800 font-medium text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t === "team" ? "Team" : "Private"}
              <span className="ml-1.5 text-xs text-slate-400">
                {deals.filter((d) => (d.visibility === "private") === (t === "private")).length}
              </span>
            </button>
          ))}
        </div>
      )}
      {privateTab && (
        <p className="text-xs text-slate-500 mb-3">
          Only you can see these deals. They don't appear for anyone else, they can't be
          shared by link, and nothing here posts to Slack.
        </p>
      )}
      {isAdmin && <AdminVerifyUser />}
      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : error ? (
        <div className="card p-8 text-center">
          <p className="text-red-600 text-sm">{error}</p>
          {errorIsAuth ? (
            // The token was rejected. Sign out BEFORE going to /login: a
            // reload reuses the same rejected token, and navigating while a
            // still-"verified" user object lingers makes LoginPage bounce
            // straight back to "/" (and the same error). signOut() clears the
            // session so the login form actually renders and re-auth is
            // reachable.
            <button
              className="btn-secondary mt-4"
              onClick={async () => {
                try {
                  await signOut();
                } catch {
                  /* fall through to the navigation regardless */
                }
                window.location.assign("/login");
              }}
            >
              Sign in again
            </button>
          ) : (
            <button className="btn-secondary mt-4" onClick={() => window.location.reload()}>
              Try again
            </button>
          )}
        </div>
      ) : visibleDeals.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          {privateTab
            ? 'No private deals yet. Click "New deal" and check Private.'
            : 'No deals yet. Click "New deal" to start.'}
        </div>
      ) : (
        <ul className="grid gap-2">
          {visibleDeals.map((d) => (
            <li key={d.id}>
              <Link
                to={`/deals/${d.id}`}
                className="card p-4 flex items-center justify-between hover:border-slate-400 hover:shadow transition"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {canCreatePrivateDeals && d.visibility === "private" && (
                      <span className="mr-1.5 text-slate-400" title="Private — only you can see this deal">
                        &#128274;
                      </span>
                    )}
                    {d.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {d.dealType} · created by {d.createdByName ?? d.createdByEmail}
                  </div>
                </div>
                <div className="text-xs text-slate-500 text-right shrink-0 ml-4">
                  {isAdmin && <div>${d.totalCostUsd.toFixed(2)}</div>}
                  <div>{new Date(d.updatedAt).toLocaleDateString()}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {showNew && (
        <NewDealModal
          allowPrivate={canCreatePrivateDeals}
          // Opening "New deal" from the Private tab defaults to private —
          // the tab you're standing on is the one you meant.
          defaultPrivate={privateTab}
          onClose={() => setShowNew(false)}
          onCreated={(d) => {
            setDeals((prev) => [d, ...prev]);
            if (canCreatePrivateDeals) setTab(d.visibility === "private" ? "private" : "team");
          }}
        />
      )}
    </div>
  );
}

/**
 * Admin-only escape hatch: mark a teammate's email verified when Firebase's
 * verification mail can't be delivered (e.g. an outside-counsel domain that
 * filters it). Tucked into a collapsed <details> so it's out of the way.
 */
function AdminVerifyUser() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.verifyUserEmail(e);
      setMsg({
        ok: true,
        text: r.alreadyVerified
          ? `${r.email} was already verified.`
          : `${r.email} is now verified. They can click "I've verified — continue" (or refresh) to get in.`,
      });
      setEmail("");
    } catch (err) {
      setMsg({ ok: false, text: friendlyApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="card p-3 mb-4">
      <summary className="cursor-pointer text-sm font-medium text-slate-700">
        Admin: verify a teammate's email
      </summary>
      <p className="text-xs text-slate-500 mt-2">
        For outside counsel who can't receive the Firebase verification email. Marks
        their account verified directly — they must have signed in at least once first.
      </p>
      <div className="flex gap-2 mt-2">
        <input
          className="input flex-1"
          type="email"
          placeholder="name@capital-law.com"
          value={email}
          disabled={busy}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        <button className="btn-secondary" onClick={() => void submit()} disabled={busy || !email.trim()}>
          {busy ? "Verifying…" : "Verify"}
        </button>
      </div>
      {msg && (
        <p className={`mt-2 text-xs ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>
      )}
    </details>
  );
}

function NewDealModal({
  allowPrivate,
  defaultPrivate,
  onClose,
  onCreated,
}: {
  allowPrivate: boolean;
  defaultPrivate: boolean;
  onClose: () => void;
  onCreated: (d: Deal) => void;
}) {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [ticker, setTicker] = useState("");
  const [dealType, setDealType] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState(allowPrivate && defaultPrivate);

  const canSubmit = title.trim().length > 0 && dealType.trim().length > 0 && !busy;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
      <div className="card w-full max-w-md p-6">
        <h2 className="font-semibold text-lg mb-4">New deal</h2>

        <label className="block text-sm font-medium mb-1">Title</label>
        <input
          className="input mb-3"
          placeholder="ATHR Note"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label className="block text-sm font-medium mb-1">
          Ticker <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <input
          className="input mb-3 font-mono"
          placeholder="ATHR"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
        />

        <label className="block text-sm font-medium mb-1">Deal type</label>
        <input
          className="input mb-3"
          placeholder={DEAL_TYPE_PLACEHOLDER}
          value={dealType}
          onChange={(e) => setDealType(e.target.value)}
        />

        <label className="block text-sm font-medium mb-1">
          Notes <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <textarea
          className="input mb-1 resize-none"
          rows={4}
          maxLength={MAX_NOTES_CHARS}
          placeholder="Anything for the reviewer to know — special instructions, prior-round context, things to watch for…"
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES_CHARS))}
        />
        <div className="text-right text-xs text-slate-400 mb-4">
          {notes.length} / {MAX_NOTES_CHARS}
        </div>

        {allowPrivate && (
          <label className="flex items-start gap-2 mb-4 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            <span>
              <span className="font-medium">Private</span>
              <span className="block text-xs text-slate-500">
                Only you can see this deal, it can't be shared by link, and it never posts
                to Slack. This can't be changed after the deal is created.
              </span>
            </span>
          </label>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!canSubmit}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const { deal } = await api.createDeal({
                  title: title.trim(),
                  ticker: ticker.trim() || undefined,
                  dealType: dealType.trim(),
                  notes: notes.trim() || undefined,
                  ...(isPrivate ? { visibility: "private" as const } : {}),
                });
                onCreated(deal);
                nav(`/deals/${deal.id}`);
              } catch (e) {
                setError(friendlyApiError(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
